import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { holidayAPI, attendanceAPI, tenantAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Plus, Loader2, Trash2, Upload, Save, CalendarDays } from "lucide-react";

const WEEKDAYS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

const HolidaysPage = () => {
  const { isSuperAdmin } = useAuth();
  const fileInputRef = useRef(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [weeklyOffs, setWeeklyOffs] = useState([5, 6]);
  const [validity, setValidity] = useState(30);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCal, setSavingCal] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // For SuperAdmin we need a tenant to operate on
  const tenantParam = isSuperAdmin ? selectedTenant : undefined;
  const ready = !isSuperAdmin || !!selectedTenant;

  useEffect(() => {
    if (isSuperAdmin) {
      tenantAPI.getAll().then((res) => {
        setTenants(res.data);
        if (res.data.length > 0) setSelectedTenant(res.data[0].tenant_id);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ready) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = tenantParam ? { tenant_id: tenantParam } : {};
      const [calRes, holRes] = await Promise.all([
        attendanceAPI.getCalendar(params),
        holidayAPI.getAll(params),
      ]);
      setWeeklyOffs(calRes.data.weekly_offs || [5, 6]);
      setValidity(calRes.data.comp_off_validity_days || 30);
      setHolidays(holRes.data);
    } catch (error) {
      toast.error("Failed to load calendar settings");
    } finally {
      setLoading(false);
    }
  };

  const toggleWeekday = (value) => {
    setWeeklyOffs((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value].sort((a, b) => a - b)
    );
  };

  const handleSaveCalendar = async () => {
    setSavingCal(true);
    try {
      await attendanceAPI.updateCalendar({
        ...(tenantParam ? { tenant_id: tenantParam } : {}),
        weekly_offs: weeklyOffs,
        comp_off_validity_days: Number(validity),
      });
      toast.success("Calendar settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingCal(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await holidayAPI.create({
        ...(tenantParam ? { tenant_id: tenantParam } : {}),
        date: newHoliday.date,
        name: newHoliday.name,
      });
      toast.success("Holiday added");
      setShowAddDialog(false);
      setNewHoliday({ date: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add holiday");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await holidayAPI.delete(id);
      toast.success("Holiday removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove holiday");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await holidayAPI.upload(formData, tenantParam);
      toast.success(`${res.data.added} holiday(s) added, ${res.data.skipped} skipped`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="holidays-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Holidays &amp; Calendar</h1>
          <p className="text-slate-500">Configure weekly offs, comp-off validity and the yearly holiday list</p>
        </div>
        {isSuperAdmin && (
          <div className="w-64">
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger data-testid="tenant-select">
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name} ({t.tenant_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!ready ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Select a tenant to configure its calendar.</CardContent></Card>
      ) : loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8" /></div>
      ) : (
        <>
          {/* Weekly Offs & Comp-off Validity */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Weekly Offs &amp; Comp-off Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-3 block">Weekly Off Days</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {WEEKDAYS.map((d) => (
                    <label key={d.value} className="flex items-center gap-2 cursor-pointer" data-testid={`weekoff-${d.value}`}>
                      <Checkbox checked={weeklyOffs.includes(d.value)} onCheckedChange={() => toggleWeekday(d.value)} />
                      <span className="text-sm">{d.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">Employees won&apos;t be asked to regularize these days. Working on them earns a comp-off.</p>
              </div>
              <div className="max-w-xs">
                <Label>Comp-off Validity (days)</Label>
                <Input type="number" min={1} max={365} value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="compoff-validity-input" />
                <p className="text-xs text-slate-400 mt-1">Employees must use earned comp-offs within this many days.</p>
              </div>
              <Button onClick={handleSaveCalendar} disabled={savingCal} className="btn-primary" data-testid="save-calendar-btn">
                {savingCal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </CardContent>
          </Card>

          {/* Holidays */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Holiday List</CardTitle>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleUpload} className="hidden" data-testid="holiday-csv-input" />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-holidays-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload CSV
                </Button>
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-holiday-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Holiday
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Add Holiday</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddHoliday} className="space-y-4 mt-4">
                      <div className="form-group">
                        <Label>Date</Label>
                        <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} required data-testid="holiday-date-input" />
                      </div>
                      <div className="form-group">
                        <Label>Holiday Name</Label>
                        <Input value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} required placeholder="e.g. Independence Day" data-testid="holiday-name-input" />
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={addLoading} data-testid="submit-holiday">
                        {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Holiday"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <p className="px-6 pb-3 text-xs text-slate-400">CSV format: two columns named <code>date</code> (YYYY-MM-DD) and <code>name</code>.</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Holiday</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        <CalendarDays className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        No holidays configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    holidays.map((h) => (
                      <TableRow key={h.id} data-testid={`holiday-row-${h.id}`}>
                        <TableCell className="font-medium">{formatDate(h.date)}</TableCell>
                        <TableCell>{new Date(h.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" })}</TableCell>
                        <TableCell>{h.name}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteHoliday(h.id)} data-testid={`delete-holiday-${h.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HolidaysPage;
